import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const sessionVersion = useRef(0);
  const appearanceVersion = useRef({ panelPalette: 0, panelBackground: 0, panelTextColor: 0 });

  useEffect(() => {
    let active = true;
    const version = sessionVersion.current;
    // Проверка наличия токена в localStorage
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
        try {
          // Проверка валидности токена и получение данных пользователя
          const isValid = await authService.validateToken(storedToken);
          if (!active || sessionVersion.current !== version) return;
          if (isValid) {
            const userData = await authService.getCurrentUser();
            if (active && sessionVersion.current === version) setCurrentUser(userData);
          } else {
            // Токен может быть протухшим, но не удаляем его автоматически,
            // чтобы не выкидывать пользователя со страницы (редирект произойдет при реальном 401 по месту)
            setCurrentUser(null);
          }
        } catch (error) {
          console.error('Ошибка при загрузке пользователя:', error);
          // В случае ошибки валидации тоже не удаляем токен, просто продолжаем
          if (active && sessionVersion.current === version) setCurrentUser(null);
        }
      }
      if (active && sessionVersion.current === version) setLoading(false);
    };
    
    loadUser();
    return () => { active = false; };
  }, []);

  // Функция входа
  const login = async (username, password) => {
    try {
      // Запрос к API для входа
      const response = await authService.login(username, password);
      
      if (response.success) {
        sessionVersion.current += 1;
        setToken(response.token);
        setCurrentUser(response.user);
      }
      
      return response;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Функция выхода
  const logout = () => {
    sessionVersion.current += 1;
    // Удаляем токен из localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    setToken(null);
    setCurrentUser(null);
  };

  // Проверка аутентификации пользователя
  const isAuthenticated = () => {
    // Считаем пользователя аутентифицированным, если есть валидный токен
    // (даже если currentUser еще не загружен)
    return !!token;
  };

  // Принудительное обновление данных текущего пользователя
  const refreshUser = async () => {
    const version = sessionVersion.current;
    const revisions = { ...appearanceVersion.current };
    try {
      const userData = await authService.getCurrentUser();
      if (!userData?.id || sessionVersion.current !== version) return null;
      setCurrentUser(user => {
        if (sessionVersion.current !== version) return user;
        return {
          ...userData,
          ...(appearanceVersion.current.panelPalette !== revisions.panelPalette ? { panelPalette: user?.panelPalette } : {}),
          ...(appearanceVersion.current.panelBackground !== revisions.panelBackground ? { panelBackground: user?.panelBackground } : {}),
          ...(appearanceVersion.current.panelTextColor !== revisions.panelTextColor ? { panelTextColor: user?.panelTextColor } : {}),
        };
      });
      return userData;
    } catch (error) {
      console.error('Ошибка при обновлении пользователя:', error);
      return null;
    }
  };

  const updateAppearance = async (field, value, save) => {
    const version = sessionVersion.current;
    const savedValue = await save(value);
    if (sessionVersion.current !== version) return;
    appearanceVersion.current[field] += 1;
    setCurrentUser(user => user && sessionVersion.current === version
      ? { ...user, [field]: savedValue }
      : user);
  };

  const updatePanelPalette = value => updateAppearance('panelPalette', value, authService.updatePanelPalette);
  const updatePanelBackground = value => updateAppearance('panelBackground', value, authService.updatePanelBackground);
  const updatePanelTextColor = value => updateAppearance('panelTextColor', value, authService.updatePanelTextColor);

  const value = {
    currentUser,
    updatePanelPalette,
    updatePanelBackground,
    updatePanelTextColor,
    login,
    logout,
    isAuthenticated,
    refreshUser,
    token
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
